import React, { useMemo } from "react";
import {
  AbsoluteFill,
  interpolate,
  useCurrentFrame,
  Easing,
} from "remotion";
import { z } from "zod";
import CroppedImg from "../components/CroppedImg";
import CroppedVideo from "../components/CroppedVideo";

const isVideoSrc = (src) => {
  if (!src || typeof src !== "string") return false;
  const clean = src.split(/[?#]/)[0];
  return /\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/i.test(clean);
};

const normalizeMediaItem = (item) => {
  if (typeof item === "string") {
    return {
      src: item,
      type: isVideoSrc(item) ? "video" : "image",
    };
  }

  const src = item?.src || "";
  const type = item?.type || (isVideoSrc(src) ? "video" : "image");

  return {
    ...item,
    src,
    type,
  };
};

// Schema for props validation
export const DynamicImageGridPropsSchema = z.object({
  images: z
    .array(
      z.union([
        z.string(),
        z.object({
          src: z.string(),
          type: z.enum(["image", "video"]).optional(),
          crop: z.object({
            x: z.number(),
            y: z.number(),
            width: z.number(),
            height: z.number(),
            sourceWidth: z.number().optional(),
            sourceHeight: z.number().optional(),
          }).nullable().optional(),
          videoStartFrom: z.number().optional(),
          videoEndAt: z.number().optional(),
        }),
      ])
    )
    .min(1)
    .describe("Array of media items (image/video) with optional crop and trim"),
  durationInFrames: z
    .number()
    .min(30)
    .default(900)
    .describe("Total animation duration in frames"),
  revealDuration: z
    .number()
    .min(10)
    .default(60)
    .describe("Duration for each image reveal animation"),
  hideDuration: z
    .number()
    .min(10)
    .default(45)
    .describe("Duration for each image hide animation"),
  finalRevealDuration: z
    .number()
    .min(30)
    .default(90)
    .describe("Duration for final all-images reveal"),
  gridPadding: z
    .number()
    .min(0)
    .default(8)
    .describe("Padding between grid items in pixels"),
  backgroundColor: z
    .string()
    .default("#000000")
    .describe("Background color of the grid"),
  borderRadius: z
    .number()
    .min(0)
    .default(8)
    .describe("Border radius for grid items"),
  animationSeed: z
    .number()
    .default(42)
    .describe("Seed for random image selection and animation sequences"),
  finalShuffleCount: z
    .number()
    .min(1)
    .max(12)
    .default(8)
    .describe("Number of different shuffles during final reveal phase"),
  finalShuffleSpeed: z
    .number()
    .min(0.1)
    .max(10)
    .default(1)
    .describe("Speed multiplier for final reveal shuffling (higher = faster)"),
});

// Calculate optimal grid dimensions based on image count - only show complete rows
const calculateGridDimensions = (imageCount) => {
  if (imageCount <= 1) return { cols: 1, rows: 1, visibleImages: 1 };
  if (imageCount <= 4) return { cols: 2, rows: 2, visibleImages: 4 };
  if (imageCount <= 9) return { cols: 3, rows: 3, visibleImages: 9 };
  
  // Max 4x4 grid (16 images) - only show complete rows
  if (imageCount <= 16) {
    if (imageCount >= 16) return { cols: 4, rows: 4, visibleImages: 16 };
    if (imageCount >= 12) return { cols: 4, rows: 3, visibleImages: 12 };
    if (imageCount >= 8) return { cols: 4, rows: 2, visibleImages: 8 };
    return { cols: 4, rows: 1, visibleImages: 4 }; // fallback for 5-7 images
  }
  
  // For more than 16 images, cap at 4x4
  return { cols: 4, rows: 4, visibleImages: 16 };
};

// Seeded random number generator for consistent animations
const seededRandom = (seed) => {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % Math.pow(2, 32);
    return state / Math.pow(2, 32);
  };
};

// Shuffle array using seeded random
const shuffleArray = (array, seed) => {
  const shuffled = [...array];
  const random = seededRandom(seed);
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

// Generate unique shuffle combinations without duplicates
const generateUniqueShuffles = (imagePool, gridSize, shuffleCount, seed) => {
  const shuffles = [];
  const usedImages = new Set();
  
  for (let i = 0; i < shuffleCount; i++) {
    const availableImages = imagePool.filter(img => !usedImages.has(img));
    
    // If we don't have enough unique images left, reset the used set
    if (availableImages.length < gridSize) {
      usedImages.clear();
      availableImages.length = 0;
      availableImages.push(...imagePool);
    }
    
    // Select unique images for this shuffle
    const selectedForShuffle = shuffleArray(availableImages, seed + i * 1000).slice(0, gridSize);
    
    // Mark these images as used
    selectedForShuffle.forEach(img => usedImages.add(img));
    
    shuffles.push(selectedForShuffle);
  }
  
  return shuffles;
};

export const DynamicImageGrid = ({
  images = [],
  durationInFrames = 900,
  revealDuration = 60,
  hideDuration = 45,
  finalRevealDuration = 90,
  gridPadding = 8,
  backgroundColor = "#000000",
  borderRadius = 8,
  animationSeed = 42,
  finalShuffleCount = 8,
  finalShuffleSpeed = 1,
}) => {
  const frame = useCurrentFrame();
  const normalizedImages = useMemo(() => images.map(normalizeMediaItem), [images]);
  
  // Memoize grid calculations
  const gridData = useMemo(() => {
    const imageCount = normalizedImages.length;
    const { cols, rows, visibleImages } = calculateGridDimensions(imageCount);
    
    // Randomly select and shuffle images from the full array
    const shuffledImages = shuffleArray(normalizedImages, animationSeed);
    const displayImages = shuffledImages.slice(0, visibleImages);
    
    // For shuffling, use all available images if we have enough unique ones
    const shufflePool = normalizedImages.length >= visibleImages * 2 ? normalizedImages : displayImages;
    
    // Pre-generate unique shuffles for final reveal
    const uniqueShuffles = generateUniqueShuffles(shufflePool, visibleImages, finalShuffleCount, animationSeed + 5000);
    
    console.log(`🎬 Grid: ${visibleImages} slots, ${normalizedImages.length} total images, ${shufflePool.length} in shuffle pool`);
    console.log(`🔀 Generated ${uniqueShuffles.length} unique shuffles`);
    
    // Create shuffled indices for animation sequence
    const imageIndices = Array.from({ length: visibleImages }, (_, i) => i);
    const shuffledIndices = shuffleArray(imageIndices, animationSeed + 1000); // Different seed for animation order
    
    // Calculate timing phases
    const cycleCount = Math.max(3, Math.floor(visibleImages / 3)); // At least 3 cycles
    const cycleDuration = revealDuration + hideDuration;
    const totalCycleTime = cycleCount * cycleDuration;
    const finalRevealStart = durationInFrames - finalRevealDuration;
    
    return {
      cols,
      rows,
      imageCount: visibleImages,
      displayImages,
      shufflePool, // Add shuffle pool for final reveal
      uniqueShuffles, // Pre-generated unique shuffles
      shuffledIndices,
      cycleCount,
      cycleDuration,
      totalCycleTime,
      finalRevealStart,
    };
  }, [normalizedImages, revealDuration, hideDuration, finalRevealDuration, durationInFrames, animationSeed, finalShuffleCount]);

  // Calculate grid item dimensions
  const itemWidth = (1920 - (gridData.cols + 1) * gridPadding) / gridData.cols;
  const itemHeight = (1080 - (gridData.rows + 1) * gridPadding) / gridData.rows;

  // Generate grid items
  const gridItems = useMemo(() => {
    return gridData.displayImages.map((mediaItem, index) => {
      const col = index % gridData.cols;
      const row = Math.floor(index / gridData.cols);
      
      const x = gridPadding + col * (itemWidth + gridPadding);
      const y = gridPadding + row * (itemHeight + gridPadding);
      
      return {
        mediaItem,
        index,
        x,
        y,
        col,
        row,
      };
    });
  }, [gridData.displayImages, gridData.cols, itemWidth, itemHeight, gridPadding]);

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {gridItems.map((item) => {
        // Determine if this image should be visible based on animation phase
        let opacity = 0;
        let currentMediaItem = item.mediaItem;
        
        if (frame >= gridData.finalRevealStart) {
          // Final reveal phase with dynamic shuffling using unique images
          const revealProgress = (frame - gridData.finalRevealStart) / finalRevealDuration;
          
          // Create shuffling effect during first 70% of final reveal using pre-generated unique shuffles
          if (revealProgress < 0.7) {
            const shufflePhase = Math.floor(revealProgress * gridData.uniqueShuffles.length * finalShuffleSpeed);
            const clampedPhase = Math.min(shufflePhase, gridData.uniqueShuffles.length - 1);
            const currentShuffle = gridData.uniqueShuffles[clampedPhase];
            currentMediaItem = currentShuffle[item.index] || item.mediaItem;
          }
          
          opacity = interpolate(
            revealProgress,
            [0, 1],
            [0, 1],
            { extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) }
          );
        } else {
          // Cycling phase - images flash in and out
          const shuffleIndex = gridData.shuffledIndices.indexOf(item.index);
          const cyclePhase = Math.floor(frame / gridData.cycleDuration);
          const frameInCycle = frame % gridData.cycleDuration;
          
          // Determine which images should be visible in this cycle
          const imagesPerCycle = Math.max(2, Math.floor(gridData.imageCount / 4));
          const totalCycles = Math.ceil(gridData.imageCount / imagesPerCycle);
          const currentCycle = cyclePhase % totalCycles;
          
          const cycleStartIndex = currentCycle * imagesPerCycle;
          const cycleEndIndex = Math.min(cycleStartIndex + imagesPerCycle, gridData.imageCount);
          
          let shouldBeVisible = false;
          shouldBeVisible = shuffleIndex >= cycleStartIndex && shuffleIndex < cycleEndIndex;
          
          if (shouldBeVisible) {
            if (frameInCycle < revealDuration) {
              // Reveal animation
              const revealProgress = frameInCycle / revealDuration;
              opacity = interpolate(
                revealProgress,
                [0, 1],
                [0, 1],
                { extrapolateRight: "clamp", easing: Easing.out(Easing.back(1.7)) }
              );
            } else {
              // Hide animation
              const hideProgress = (frameInCycle - revealDuration) / hideDuration;
              opacity = interpolate(
                hideProgress,
                [0, 1],
                [1, 0],
                { extrapolateRight: "clamp", easing: Easing.in(Easing.cubic) }
              );
            }
          }
        }

        // Add subtle scale animation for reveal
        const scale = interpolate(
          opacity,
          [0, 1],
          [0.9, 1],
          { extrapolateRight: "clamp" }
        );

        return (
          <div
            key={item.index}
            style={{
              position: "absolute",
              left: item.x,
              top: item.y,
              width: itemWidth,
              height: itemHeight,
              opacity,
              transform: `scale(${scale})`,
              borderRadius: borderRadius,
              overflow: "hidden",
              boxShadow: opacity > 0.1 ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "none",
              transition: "box-shadow 0.3s ease",
            }}
          >
            {currentMediaItem?.src ? (
              currentMediaItem.type === "video" ? (
                <CroppedVideo
                  src={currentMediaItem.src}
                  crop={currentMediaItem.crop}
                  startFrom={currentMediaItem.videoStartFrom}
                  endAt={currentMediaItem.videoEndAt}
                  muted
                />
              ) : (
                <CroppedImg
                  src={currentMediaItem.src}
                  crop={currentMediaItem.crop}
                />
              )
            ) : null}
          </div>
        );
      })}
      
    </AbsoluteFill>
  );
};

export default DynamicImageGrid;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
